import { NotImplementedError } from "../../../../errors";
import { parseDateFilter } from "../../../../models/looker/modules/overview";
import { PromiseHandler } from "../../../../handlers";
/**
 * Typed handler for the Overview dashboard Creatives widget. Unlike the
 * other Overview handlers, the field set requested is caller-supplied via
 * `filters.fields` rather than fixed — the Creatives widget's product
 * registry (smithers) computes which fields are needed from whichever
 * product types are currently enabled, so the query shape genuinely varies.
 * Rows are returned raw/unparsed; matching a row to its product type and
 * shaping it for rendering is the registry's job, not this handler's.
 */
export default class LookerOverviewCreatives extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-overview-creatives");
    }
    async findItems(filters) {
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        const fields = filters.fields;
        if (!fields?.length) {
            throw new Error("Fields is required");
        }
        const queryBody = {
            model: "omnichannel_analytics",
            view: "explore_overview",
            fields,
            filters: {
                "explore_overview.flt_date_range": parseDateFilter(dateFilter),
                // Only real creatives — exclude null billable creative ids.
                "creative_detail.billable_creative_id": "-NULL"
            },
            sorts: ["explore_overview.total_impressions desc"],
            // Unlimited (matches the sibling overview list queries): the widget
            // paginates client-side over the full result set, so a fixed cap would
            // silently make rows beyond it unreachable.
            limit: "-1"
        };
        const lookbackFilter = filters.where?.find((filter) => filter.field === "lookbackDays");
        if (lookbackFilter?.value !== undefined && lookbackFilter.value !== null) {
            queryBody.filters["explore_overview.pmt_lookback_window"] = String(lookbackFilter.value);
        }
        const mediaTypeFilter = filters.where?.find((filter) => filter.field === "mediaType");
        if (mediaTypeFilter?.value) {
            queryBody.filters["explore_overview.product_group"] = mediaTypeFilter.value;
        }
        // Fail closed: without both filters the query would aggregate across
        // every partner, so we refuse to run it rather than leak cross-tenant
        // data (matches the sibling top-line handler).
        const advertiserFilter = filters.where?.find((filter) => filter.field === "advertiserIds");
        const advertiserIds = advertiserFilter?.value;
        if (!advertiserIds?.length) {
            throw new Error("Advertiser ids filter is required");
        }
        queryBody.filters["meta_inst.advertiser_id"] = advertiserIds.join(",");
        const campaignFilter = filters.where?.find((filter) => filter.field === "campaignIds");
        const campaignIds = campaignFilter?.value;
        if (!campaignIds?.length) {
            throw new Error("Campaign ids filter is required");
        }
        queryBody.filters["meta_inst.campaign_id"] = campaignIds.join(",");
        const hashKey = await this.sdk.looker.query.hashQuery(queryBody);
        return this.cache.promise(hashKey, () => this.sdk.looker.query
            .run(queryBody)
            .then(({ data, queryMeta }) => [{ data, queryMeta }])
            .catch((e) => {
            console.error(e);
            throw new Error("Failed to fetch overview dashboard creatives");
        }));
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
//# sourceMappingURL=Creatives.js.map